import flatten from 'lodash.flatten';
import groupBy from 'lodash.groupby';
import React from 'react';
import withSideEffect from 'react-side-effect';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {gettext, interpolate} from 'utils/text';
import getCurrency from 'utils/getCurrency';
import reverseUrl from 'utils/urls';

import EmptyCart from './EmptyCart';
import QuantitySelect from '../QuantitySelect';
import {addItem, fetchCart, removeItem} from '../../ducks/basket';
import {BasketPropType, LinesPropType} from '../../utils/propTypes';
import {integerSort} from '../../utils/sorting';


const FREE_SHIPPING_THRESHOLD = 150;  // euros


class FullCart extends React.Component {
    static propTypes = {
        isLoading: PropTypes.bool.isRequired,
        basket: BasketPropType.isRequired,
        lines: LinesPropType.isRequired,

        onLoad: PropTypes.func.isRequired,
        onRemoveLine: PropTypes.func.isRequired,
        onUpdateAmount: PropTypes.func.isRequired,
    };

    static defaultProps = {
        basket: {},
        lines: [],
    };

    constructor(props) {
        super(props);
        this.state = {
            hasLoadedOnce: !props.isLoading,
        };
    }

    componentDidMount() {
        this.props.onLoad();
    }

    componentWillReceiveProps(nextProps) {
        if (!nextProps.isLoading) {
            this.setState({hasLoadedOnce: true});
        }
    }

    removeLine = (line) => {
        this.props.onRemoveLine(line);
    };

    renderPrice = (price, currency) => {
        if (price.is_discounted) {
            return (
                <div>
                    <div className="price__discount">{price.excl_tax.toFixed(2)}{getCurrency(currency)}</div>
                    <div className="price__strikethrough">{price.orig_excl_tax.toFixed(2)}{getCurrency(currency)}</div>
                </div>
            );
        } else {
            return (
                <div>{price.excl_tax.toFixed(2)}{getCurrency(currency)}</div>
            );
        }
    };

    renderLine = (line) => {
        const productUrl = reverseUrl('catalogue:detail', line.product.slug, line.product.id);

        const imgUrl = line.product.images.length > 0 ? line.product.images[0].original : DJ_CONST.defaultProductImage;

        // TODO: fix after removing `unlimited products' feature
        // const maxQuantity = line.product.available_amount.num_available;
        const maxQuantity = null;
        const currentQtt = line.quantity;

        return (
            <tr key={line.product.id}>
                <td>
                    <a className="basket-line-remove" onClick={() => this.removeLine(line)}>
                        <i className="icon icon--close" />
                    </a>
                    <a className="basket-line-thumbnail" href={productUrl}>
                        <img src={imgUrl} alt={line.product.title} />
                    </a>
                    <a className="basket-line-title" href={productUrl}>{line.product.title}</a>
                </td>
                <td className="text-center">
                    {this.renderPrice(line.product.price, line.price_currency)}
                </td>
                <td className="text-center">
                    <div className="basket-line-qtt">
                        <QuantitySelect
                            value={currentQtt}
                            maxValue={maxQuantity}
                            updateAmountFunction={counter => this.props.onUpdateAmount(line, counter)}
                            shouldQuantityChange
                        />
                    </div>
                </td>
                <td className="text-center">{line.price_excl_tax}{getCurrency(line.price_currency)}</td>
            </tr>
        );
    };

    renderTaxGroup = (taxRate, lines) => (
        <table key={`${taxRate}-table`} className="mb-5">
            <thead key={`${taxRate}-head`}>
                <tr>
                    <th scope="col" colSpan="4">
                        <h3 className="basket-group-divider">{gettext('VAT')} {taxRate}%</h3>
                    </th>
                </tr>
                <tr>
                    <th scope="col">{gettext('Product')}</th>
                    <th scope="col" className="text-center">{gettext('Price')}</th>
                    <th scope="col" className="text-center">{gettext('Quantity')}</th>
                    <th scope="col" className="text-center">{gettext('Total')}</th>
                </tr>
            </thead>
            <tbody key={`${taxRate}-body`}>
                {lines.map(line => this.renderLine(line))}
            </tbody>
        </table>
    );

    renderTaxGroups = (lines) => {
        const taxGroups = groupBy(lines, line => parseInt(line.product.price.tax_rate * 100, 10));
        const rowGroups = Object.keys(taxGroups).sort(integerSort).map(taxRate => (
            this.renderTaxGroup(taxRate, taxGroups[taxRate])
        ));

        return (
            <div className="basket-table">
                {flatten(rowGroups)}
            </div>
        );
    };

    renderTaxes = (lines, currency) => {
        const taxesText = gettext("VAT %s%:");
        const taxGroups = groupBy(lines, line => parseInt(line.product.price.tax_rate * 100, 10));

        const isShippingFree = this.props.basket.total_excl_tax >= FREE_SHIPPING_THRESHOLD;

        // if there is no 22% tax group, create one to show standard shipping tax
        if (!isShippingFree && !(22 in taxGroups)) {
            taxGroups[22] = [{price_incl_tax: 0, price_excl_tax: 0}];
        }

        return Object.keys(taxGroups).sort(integerSort).map((taxRate) => {
            const groupTaxes = taxGroups[taxRate].map(
                line => parseFloat(line.price_incl_tax) - parseFloat(line.price_excl_tax),
            );
            // add standard shipping tax to tax line only if the tax rate is 22
            const isTaxTwenty = taxRate === '22';
            const standardShippingTax = (isShippingFree || !isTaxTwenty)
                ? 0
                : parseFloat(this.props.basket.shipping_price.tax);
            const groupSum = groupTaxes.reduce((a, b) => a + b, 0);
            const totalSum = groupSum + standardShippingTax;
            return (
                <span key={`${taxRate}-tax`}>
                    <span>{interpolate(taxesText, [taxRate])}</span>
                    <span>{totalSum.toFixed(2) + currency}</span>
                </span>
            );
        });
    };

    renderCartTotals = () => {
        const {basket, lines} = this.props;
        const currency = getCurrency(basket.currency);

        const total = basket.total_excl_tax;
        const remaining = (FREE_SHIPPING_THRESHOLD - total).toFixed(2) + currency;
        const remainingStr = gettext("Add %s worth of products to your basket to earn free shipping");

        const isShippingFree = total >= FREE_SHIPPING_THRESHOLD;
        const shippingText = isShippingFree ?
            gettext("Shipping is free for this order.") :
            interpolate(remainingStr, [remaining]);

        const shippingCost = (
            <span>
                <span>{gettext('Shipping:')}</span>
                <span>{basket.shipping_price.excl_tax + currency}</span>
            </span>
        );

        let totalInclTax = parseFloat(basket.total_incl_tax);

        if (!isShippingFree) {
            totalInclTax += parseFloat(basket.shipping_price.incl_tax);
        }

        return (
            <div className="border-box-rounded">
                <div className="box-content">
                    <span>
                        <span>{gettext('Basket:')}</span>
                        <span>{basket.total_excl_tax + currency}</span>
                    </span>
                    {!isShippingFree && shippingCost}
                    {this.renderTaxes(lines, currency)}
                </div>
                <hr />
                <div className="box-content">
                    <span>
                        <span>{gettext('Total payment:')}</span>
                        <span>{totalInclTax.toFixed(2) + currency}</span>
                    </span>
                    <a href={reverseUrl('checkout:index')} className="btn btn-default btn-lg">
                        {gettext('Proceed to checkout')}
                    </a>
                </div>
                <div className="shipping-info">
                    {shippingText}
                </div>
            </div>
        );
    };

    renderCart(lines) {
        return (
            <div className="row">
                <div className="col-xs-9">
                    {this.renderTaxGroups(lines)}
                </div>
                <div className="col-xs-3">
                    {this.renderCartTotals()}
                </div>
            </div>
        );
    }

    renderEmptyCart = () => <EmptyCart />;

    render() {
        const {lines} = this.props;
        // Show nothing when the basket has not been loaded and don't flicker when we are updating it.
        if (!this.state.hasLoadedOnce) {
            return null;
        }

        return (
            <div>
                {lines.length ? this.renderCart(lines) : this.renderEmptyCart()}
            </div>
        );
    }
}

function reducePropsToState(propsList) {
    let isLoading = true;
    let qtt;
    propsList.forEach((props) => {
        qtt = props.lines.length;
        isLoading = props.isLoading;
    });
    if (isLoading) {
        qtt = undefined;
    }
    return qtt;
}

function handleCounterChange(qtt) {
    if (typeof qtt !== 'undefined') {
        document.getElementById('round-counter').innerHTML = qtt;
    }
}

const mapStateToProps = state => ({
    isLoading: state.basket.isLoading,
    basket: state.basket.details,
    lines: state.basket.lines,
});

const mapDispatchToProps = dispatch => ({
    onLoad: () => dispatch(fetchCart()),
    onRemoveLine: line => dispatch(removeItem(line)),
    onUpdateAmount: (line, counter) => dispatch(addItem(line.product.id, line.product.title, counter)),
});

const FullCartWithSideEffect = withSideEffect(
    reducePropsToState,
    handleCounterChange,
)(FullCart);

const FullCartConnector = connect(
    mapStateToProps,
    mapDispatchToProps,
)(FullCartWithSideEffect);

export default FullCartConnector;



// WEBPACK FOOTER //
// ./src/components/cart/FullCart.js